import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Logger } from '@/components/Logger'
import { plexWebSocketClient } from '@/services/api/plex/PlexWebSocketClient'

interface ConnectionHealthState {
  activeConnections: number
  showBanner: boolean
  hasNoConnections: boolean
  isInGracePeriod: boolean
}

interface ConnectionHealthContextValue extends ConnectionHealthState {
  dismissBanner: () => void
  startGracePeriod: (durationMs?: number) => void
}

const ConnectionHealthContext = createContext<ConnectionHealthContextValue | null>(null)

const GRACE_PERIOD_MS = 10000
const NO_CONNECTIONS_BANNER_DELAY_MS = 4000

interface ConnectionHealthProviderProps extends PropsWithChildren {}

export function ConnectionHealthProvider({ children }: ConnectionHealthProviderProps) {
  const [activeConnections, setActiveConnections] = useState(0)
  const [hasNoConnections, setHasNoConnections] = useState(false)
  const [showBanner, setShowBanner] = useState(false)
  const [manuallyDismissed, setManuallyDismissed] = useState(false)
  const [isInGracePeriod, setIsInGracePeriod] = useState(true) // Start in grace period

  const manualDismissTimeoutRef = useRef<TimeoutHandle | null>(null)
  const gracePeriodTimeoutRef = useRef<TimeoutHandle | null>(null)
  const noConnectionsTimeoutRef = useRef<TimeoutHandle | null>(null)

  // Derived state
  const shouldShowBanner =
    activeConnections === 0 && hasNoConnections && !manuallyDismissed && !isInGracePeriod

  const startGracePeriod = useCallback((durationMs: number = GRACE_PERIOD_MS) => {
    if (gracePeriodTimeoutRef.current) {
      clearTimeout(gracePeriodTimeoutRef.current)
    }

    setIsInGracePeriod(true)
    setShowBanner(false)

    gracePeriodTimeoutRef.current = setTimeout(() => {
      setIsInGracePeriod(false)
      Logger.log(
        'DEBUG: ConnectionHealthProvider - Grace period ended, banner validation now active',
      )
    }, durationMs)
  }, [])

  // Follow the single multiplexed socket's state directly.
  useEffect(() => {
    const clearNoConnectionsTimeout = () => {
      if (noConnectionsTimeoutRef.current) {
        clearTimeout(noConnectionsTimeoutRef.current)
        noConnectionsTimeoutRef.current = null
      }
    }

    const unsubscribe = plexWebSocketClient.subscribeToConnectionHealth(
      ({ subscriptionCount, connectedSocketCount }) => {
        setActiveConnections(connectedSocketCount)

        // No subscriptions means no socket is required. One OPEN socket is
        // the complete healthy state for the multiplexed transport.
        if (subscriptionCount === 0 || connectedSocketCount === 1) {
          clearNoConnectionsTimeout()
          setHasNoConnections(false)
          return
        }

        if (noConnectionsTimeoutRef.current) return

        // A brief reconnect is expected; recheck live health after the delay before reporting an outage.
        noConnectionsTimeoutRef.current = setTimeout(() => {
          const currentHealth = plexWebSocketClient.getConnectionHealth()
          if (currentHealth.subscriptionCount > 0 && currentHealth.connectedSocketCount === 0) {
            setHasNoConnections(true)
            return
          }

          noConnectionsTimeoutRef.current = null
          setHasNoConnections(false)
        }, NO_CONNECTIONS_BANNER_DELAY_MS)
      },
    )

    return () => {
      unsubscribe()
      clearNoConnectionsTimeout()
    }
  }, [])

  // Grace period - don't show banner for first 10 seconds after page load
  useEffect(() => {
    startGracePeriod()

    return () => {
      if (gracePeriodTimeoutRef.current) {
        clearTimeout(gracePeriodTimeoutRef.current)
      }
    }
  }, [startGracePeriod])

  // Handle banner visibility changes
  useEffect(() => {
    if (shouldShowBanner && !showBanner) {
      // Show banner immediately
      //get connections status
      const connectionsStatus = plexWebSocketClient.getConnectionStatus()
      Logger.log(
        'DEBUG: ConnectionHealthProvider - Showing banner',
        'hasNoConnections',
        hasNoConnections,
        'Connections status',
        JSON.stringify(connectionsStatus, null, 2),
        'manuallyDismissed',
        manuallyDismissed,
        'shouldShowBanner',
        shouldShowBanner,
        'currentlyShowing',
        showBanner,
      )
      setShowBanner(true)
    } else if (!shouldShowBanner && showBanner) {
      Logger.log('DEBUG: ConnectionHealthProvider - Connection restored, hiding banner')
      setShowBanner(false)
    }
  }, [shouldShowBanner, showBanner, hasNoConnections, manuallyDismissed])

  // Manual dismiss function
  const dismissBanner = useCallback(() => {
    Logger.log('DEBUG: ConnectionHealthProvider - Manually dismissing banner')
    setShowBanner(false)
    setManuallyDismissed(true)

    // Clear any existing manual dismiss timeout
    if (manualDismissTimeoutRef.current) {
      clearTimeout(manualDismissTimeoutRef.current)
    }

    // Dismissal is temporary so an unresolved outage becomes visible again after 30 seconds.
    manualDismissTimeoutRef.current = setTimeout(() => {
      Logger.log('DEBUG: ConnectionHealthProvider - Resetting manual dismissal')
      setManuallyDismissed(false)
    }, 30000)
  }, [])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (manualDismissTimeoutRef.current) {
        clearTimeout(manualDismissTimeoutRef.current)
      }
    }
  }, [])

  const contextValue: ConnectionHealthContextValue = {
    activeConnections,
    showBanner,
    hasNoConnections,
    isInGracePeriod,
    dismissBanner,
    startGracePeriod,
  }

  return (
    <ConnectionHealthContext.Provider value={contextValue}>
      {children}
    </ConnectionHealthContext.Provider>
  )
}

export function useConnectionHealth() {
  const context = useContext(ConnectionHealthContext)
  if (!context) {
    throw new Error('useConnectionHealth must be used within a ConnectionHealthProvider')
  }
  return context
}
